import { useMemo } from "react";
import { Link, useLoaderData } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { StatCard } from "../../components/analytics/StatCard";
import { ExportButton } from "../../components/analytics/ExportButton";
import type { NetworkAnalyticsData } from "../../routes/loaders";
import type { TierSubscriberCount } from "../../types/networkAnalytics";
import styles from "./NetworkAnalyticsPage.module.css";

const COLORS = [
  "#6366f1",
  "#06b6d4",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMonth(month: string): string {
  const [y, m] = month.split("-");
  const d = new Date(Number(y), Number(m) - 1);
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

export default function NetworkAnalyticsPage() {
  const { network, tiers, memberships, revenueSummary, organizationId } =
    useLoaderData() as NetworkAnalyticsData;

  // Compute tier subscriber counts from memberships
  const tierSubscribers: TierSubscriberCount[] = useMemo(() => {
    const activeMemberships = memberships.filter((m) => m.status === "active");
    const tierMap = new Map<string, TierSubscriberCount>();

    for (const tier of tiers) {
      tierMap.set(tier.id, {
        tierId: tier.id,
        tierName: tier.name,
        count: 0,
        monthlyPriceCents: tier.monthlyPriceCents,
      });
    }

    for (const m of activeMemberships) {
      if (m.tierId && tierMap.has(m.tierId)) {
        tierMap.get(m.tierId)!.count++;
      }
    }

    return Array.from(tierMap.values());
  }, [tiers, memberships]);

  // MRR: sum of active subscribers * tier price
  const mrr = useMemo(
    () => tierSubscribers.reduce((sum, t) => sum + t.count * t.monthlyPriceCents, 0),
    [tierSubscribers],
  );
  const arr = mrr * 12;

  const totalSubscribers = useMemo(
    () => tierSubscribers.reduce((sum, t) => sum + t.count, 0),
    [tierSubscribers],
  );

  // Revenue over time chart data
  const revenueOverTime = useMemo(
    () =>
      revenueSummary.months.map((m) => ({
        month: m.month,
        label: formatMonth(m.month),
        grossRevenue: m.totalGrossRevenueCents / 100,
        netRevenue: m.totalNetRevenueCents / 100,
        platformFees: m.totalPlatformFeeCents / 100,
      })),
    [revenueSummary],
  );

  // Revenue breakdown by podcast (aggregate across all months)
  const podcastRevenue = useMemo(() => {
    const map = new Map<
      string,
      { podcastName: string; grossCents: number; netCents: number; subscribers: number }
    >();
    for (const month of revenueSummary.months) {
      for (const d of month.distributions) {
        const existing = map.get(d.podcastId);
        if (existing) {
          existing.grossCents += d.grossRevenueCents;
          existing.netCents += d.netRevenueCents;
          existing.subscribers = Math.max(existing.subscribers, d.subscriberCount);
        } else {
          map.set(d.podcastId, {
            podcastName: d.podcastName,
            grossCents: d.grossRevenueCents,
            netCents: d.netRevenueCents,
            subscribers: d.subscriberCount,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => b.grossCents - a.grossCents);
  }, [revenueSummary]);

  // Top 5 performing podcasts
  const topPodcasts = podcastRevenue.slice(0, 5);

  // Revenue by podcast for bar chart
  const podcastBarData = podcastRevenue.slice(0, 10).map((p) => ({
    name: p.podcastName.length > 20 ? p.podcastName.slice(0, 18) + "…" : p.podcastName,
    revenue: p.grossCents / 100,
  }));

  // Total revenue from last month for display
  const lastMonth = revenueSummary.months[revenueSummary.months.length - 1];
  const totalGrossRevenue = revenueSummary.months.reduce(
    (sum, m) => sum + m.totalGrossRevenueCents,
    0,
  );

  // Export data
  const exportData = revenueOverTime.map((r) => ({
    month: r.month,
    grossRevenue: r.grossRevenue,
    netRevenue: r.netRevenue,
    platformFees: r.platformFees,
  }));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Link to={`/organizations/${organizationId}/networks`}>Networks</Link>
          <span className={styles.sep}>/</span>
          <Link to={`/organizations/${organizationId}/networks/${network.id}/settings`}>
            {network.name}
          </Link>
          <span className={styles.sep}>/</span>
          <span>Analytics</span>
        </div>
        <h1 className={styles.title}>Network Analytics</h1>
      </div>

      <div className={styles.toolbar}>
        <div />
        <ExportButton data={exportData} filename={`${network.name}-analytics`} />
      </div>

      <div className={styles.statGrid}>
        <StatCard label="Total Subscribers" value={totalSubscribers} />
        <StatCard label="MRR" value={formatCents(mrr)} />
        <StatCard label="ARR" value={formatCents(arr)} />
        <StatCard label="Total Revenue (12mo)" value={formatCents(totalGrossRevenue)} />
        <StatCard
          label="Last Month Revenue"
          value={lastMonth ? formatCents(lastMonth.totalGrossRevenueCents) : "$0.00"}
        />
      </div>

      {/* Subscribers by Tier */}
      {tierSubscribers.length > 0 && (
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>Subscribers by Tier</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={tierSubscribers}
                dataKey="count"
                nameKey="tierName"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(props: { name?: string; value?: number }) =>
                  `${props.name ?? ""}: ${props.value ?? 0}`
                }
              >
                {tierSubscribers.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Revenue Over Time */}
      {revenueOverTime.length > 0 && (
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>Revenue Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueOverTime} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="gradGross" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `$${v}`} />
              <Tooltip formatter={(value) => `$${Number(value ?? 0).toFixed(2)}`} />
              <Legend />
              <Area
                type="monotone"
                dataKey="grossRevenue"
                name="Gross Revenue"
                stroke="#6366f1"
                fill="url(#gradGross)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="netRevenue"
                name="Net Revenue"
                stroke="#10b981"
                fill="url(#gradNet)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className={styles.splitGrid}>
        {/* Revenue by Podcast */}
        {podcastBarData.length > 0 && (
          <div className={styles.chartContainer}>
            <h3 className={styles.chartTitle}>Revenue by Podcast</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={podcastBarData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `$${v}`} />
                <Tooltip formatter={(value) => `$${Number(value ?? 0).toFixed(2)}`} />
                <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Performing Podcasts */}
        <div className={styles.chartContainer}>
          <h3 className={styles.chartTitle}>Top Performing Podcasts</h3>
          {topPodcasts.length === 0 ? (
            <p className={styles.empty}>No revenue data yet</p>
          ) : (
            <ul className={styles.topPodcastsList}>
              {topPodcasts.map((p, i) => (
                <li key={i} className={styles.topPodcastItem}>
                  <span className={styles.podcastRank}>{i + 1}</span>
                  <span className={styles.podcastName}>{p.podcastName}</span>
                  <span className={styles.podcastRevenue}>{formatCents(p.grossCents)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
